import ExcelJS from 'exceljs';
import moment from 'moment';

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const logo = {
  url: 'https://cmtsaludproduction.s3.sa-east-1.amazonaws.com/resources/logo-cmt-naranjo-2023.png',
  width: 210,
  height: 72,
};

const layout = {
  font: 'Arial',
  amountFormat: '#,##0',
  columnWidths: [15, 15, 15, 15, 50, 15, 15, 15, 15, 15, 15, 15],
  totalColumns: 12,
  amountColumns: [9, 10, 11],
  paymentMethodColumn: 8,
};

const headers = [
  'N° Reserva',
  'Fecha Reserva',
  'RUT',
  'Compañías',
  'Tipo Cliente',
  'N° Factura',
  'M. Pago',
  'Monto Reservado',
  'Monto Atendido',
  'Monto Facturado',
];

const sections = [
  { key: 'invoiced', title: 'Facturadas' },
  { key: 'notInvoiced', title: 'No Facturadas' },
  { key: 'pending', title: 'Nuevos ítems por facturar' },
];

const whiteFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };

const styles = {
  mainTitle: { font: { bold: true, size: 13, name: layout.font }, alignment: { horizontal: 'center', wrapText: false } },
  thirdTitle: { font: { bold: true, size: 9, name: layout.font }, alignment: { horizontal: 'left', wrapText: false } },
  bodyText: { font: { bold: false, size: 8, name: layout.font }, alignment: { horizontal: 'left' } },
};

const padRow = (values) => {
  const row = [...values];

  while (row.length < layout.totalColumns) {
    row.push('');
  }

  return row;
};

const addStyledRow = (worksheet, values, style) => {
  const row = worksheet.addRow(padRow(values));

  for (let column = 1; column <= layout.totalColumns; column += 1) {
    const cell = row.getCell(column);

    cell.font = style.font;
    cell.alignment = style.alignment;
    cell.fill = whiteFill;
  }

  return row;
};

const addLogo = async (workbook, worksheet) => {
  try {
    const response = await fetch(logo.url);
    const buffer = await response.arrayBuffer();
    const imageId = workbook.addImage({ buffer, extension: 'png' });

    worksheet.addImage(imageId, {
      tl: { col: 1, row: 1 },
      ext: { width: logo.width, height: logo.height },
      editAs: 'absolute',
    });
  } catch (error) {
    console.error('No se pudo incrustar el logo, el reporte se genera sin él', error);
  }
};

const addHeader = (worksheet, { branchOfficeName, startDate, endDate }) => {
  addStyledRow(worksheet, [], styles.mainTitle);
  addStyledRow(worksheet, [], styles.mainTitle);
  addStyledRow(worksheet, [], styles.mainTitle);
  addStyledRow(worksheet, ['', '', '', '', 'RESUMEN FACTURAS'], styles.mainTitle);
  addStyledRow(worksheet, [], styles.mainTitle);

  [
    ['Sucursal', branchOfficeName || ''],
    ['Desde', startDate || ''],
    ['Hasta', endDate || ''],
  ].forEach(([label, value]) => {
    addStyledRow(worksheet, ['', label, value], styles.thirdTitle);
  });
};

const addSection = (worksheet, title, rows) => {
  addStyledRow(worksheet, [], styles.bodyText);
  addStyledRow(worksheet, ['', title], styles.thirdTitle);
  addStyledRow(worksheet, ['', ...headers], styles.thirdTitle);

  rows.forEach((row) => {
    const worksheetRow = addStyledRow(worksheet, [
      '',
      row.reservationNumber,
      row.reservationDate ? moment(row.reservationDate).format('YYYY-MM-DD') : '',
      row.document,
      row.businessName,
      row.clientType,
      row.invoiceNumber,
      row.paymentMethod,
      row.reservedAmount,
      row.attendedAmount,
      row.invoicedAmount,
    ], styles.bodyText);

    layout.amountColumns.forEach((column) => {
      worksheetRow.getCell(column).numFmt = layout.amountFormat;
    });

    worksheetRow.getCell(layout.paymentMethodColumn).alignment = {
      ...styles.bodyText.alignment,
      wrapText: true,
      vertical: 'top',
    };
  });
};

const triggerDownload = (buffer, fileName) => {
  const blob = new Blob([buffer], { type: XLSX_CONTENT_TYPE });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const exportBillingReportToExcel = async (reportData, filters) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('resumen');

  worksheet.columns = layout.columnWidths.map((width) => ({ width }));

  addHeader(worksheet, { ...filters, branchOfficeName: reportData.branchOfficeName });

  sections.forEach(({ key, title }) => {
    addSection(worksheet, title, reportData[key] || []);
  });

  await addLogo(workbook, worksheet);

  const buffer = await workbook.xlsx.writeBuffer();

  triggerDownload(buffer, `cmt-facturas_${moment().format('DDMMYY_HHmm')}.xlsx`);
};
